import { Area } from '@/stores/SubprojectStore';
import {
  SQUARE_FEET_PER_ACRE,
  SQUARE_FEET_PER_HECTARE,
  SQUARE_FEET_PER_SQUARE_METER,
} from '@/utils';

// adds relativeCost, relativeEnergyDemand and limit to resources that have the fields
// capitalCostPerUnit, energyUsePerYr and unitsNeededPerSqft
export function addMetricsToResource<
  T extends {
    capitalCostPerUnit: string;
    energyUsePerYr: string;
    unitsNeededPerSqft: string;
  },
>(
  data: T[],
  conditionedSpaceArea?: Area,
): (T & {
  coveragePercentage: number;
  limit: number;
  relativeCost: 1 | 2 | 3 | 4;
  relativeEnergyDemand: 1 | 2 | 3 | 4;
})[] {
  // conditionedSpace should never be undefined if the select clause runs
  // as this query is only enabled when conditionedSpace is *not* undefined
  if (data.length === 0 || conditionedSpaceArea === undefined) {
    return [];
  }

  const conditionedSpace = convertToSqft(conditionedSpaceArea);

  // calculate the relative cost and energy demand of each waterpump
  // the relative cost / energydemand can be either 1 | 2 | 3 | 4
  const { maxCost, minCost, maxEnergyDemand, minEnergyDemand } = data.reduce(
    (acc, item) => ({
      maxCost: Math.max(
        acc.maxCost,
        Number(item.capitalCostPerUnit) * Number(item.unitsNeededPerSqft),
      ),
      minCost: Math.min(
        acc.minCost,
        Number(item.capitalCostPerUnit) * Number(item.unitsNeededPerSqft),
      ),
      maxEnergyDemand: Math.max(
        acc.maxEnergyDemand,
        Number(item.energyUsePerYr) * Number(item.unitsNeededPerSqft),
      ),
      minEnergyDemand: Math.min(
        acc.minEnergyDemand,
        Number(item.energyUsePerYr) * Number(item.unitsNeededPerSqft),
      ),
    }),
    // accumulator's initial values:
    {
      maxCost:
        Number(data[0].capitalCostPerUnit) * Number(data[0].unitsNeededPerSqft),
      minCost:
        Number(data[0].capitalCostPerUnit) * Number(data[0].unitsNeededPerSqft),
      maxEnergyDemand:
        Number(data[0].energyUsePerYr) * Number(data[0].unitsNeededPerSqft),
      minEnergyDemand:
        Number(data[0].energyUsePerYr) * Number(data[0].unitsNeededPerSqft),
    },
  );

  const costStepSize = (maxCost - minCost) / 4;
  const energyStepSize = (maxEnergyDemand - minEnergyDemand) / 4;

  return data.map((item) => ({
    ...item,
    coveragePercentage: getCoveragePercentage(item, conditionedSpace),
    limit: getUnitLimit(Number(item.unitsNeededPerSqft), conditionedSpace),
    relativeCost: getRelativeValue(
      Number(item.capitalCostPerUnit) * Number(item.unitsNeededPerSqft),
      minCost,
      costStepSize,
    ),
    relativeEnergyDemand: getRelativeValue(
      Number(item.energyUsePerYr) * Number(item.unitsNeededPerSqft),
      minEnergyDemand,
      energyStepSize,
    ),
  }));
}

/**
 * How many units of a technology a building needs: the catalog's per-sqft
 * factor times the conditioned area, rounded up to a whole unit.
 *
 * Never returns less than 1. A subproject is created with conditioned space
 * defaulted to 0, and a catalog row can carry a unit factor of 0; either way
 * the raw product is 0. A limit of 0 selects the item with 0 units and leaves
 * both stepper buttons disabled, so the count field becomes the only way to
 * change it.
 *
 * The single source of the limit rule for every technology category. HVAC
 * reaches it through getPossibleUnitsForHvac; everything else through
 * addMetricsToResource.
 */
export function getUnitLimit(
  unitsNeededPerSqft: number,
  conditionedSqft: number,
): number {
  return Math.max(1, Math.ceil(unitsNeededPerSqft * conditionedSqft));
}

export const getRelativeValue = (
  value: number,
  min: number,
  stepSize: number,
): 1 | 2 | 3 | 4 => {
  if (value < min + stepSize) return 1;
  if (value < min + stepSize * 2) return 2;
  if (value < min + stepSize * 3) return 3;

  return 4;
};

export function getCoveragePercentage<
  T extends {
    capitalCostPerUnit: string;
    energyUsePerYr: string;
    unitsNeededPerSqft: string;
  },
>(item: T, conditionedSpace: number): number {
  const coveragePercentage =
    1 / Number(item.unitsNeededPerSqft) / conditionedSpace;

  if (isNaN(coveragePercentage) || !isFinite(coveragePercentage)) {
    return 0;
  }

  return coveragePercentage;
}

// TODO? convert units to sqft in a more robust way
export function convertToSqft(conditionedSpaceArea: Area): number {
  switch (conditionedSpaceArea.unit) {
    case 'SquareMeters':
      return SQUARE_FEET_PER_SQUARE_METER * conditionedSpaceArea.value;
    case 'Acre':
      return SQUARE_FEET_PER_ACRE * conditionedSpaceArea.value;
    case 'Hectares':
      return SQUARE_FEET_PER_HECTARE * conditionedSpaceArea.value;
    case 'sqft':
      return conditionedSpaceArea.value;
    default:
      return conditionedSpaceArea.value;
  }
}
